import { Tab as HeadlessTab } from "@headlessui/react";
import classNames from "components/ui/classNames";
import type { ReactNode } from "react";
import { Fragment, useId } from "react";
import { atom, useAtom } from "jotai";

export const TabPanels = HeadlessTab.Panels;

const selectedTabIndexState = atom(0);

interface ITabsProps {
  children: ReactNode;
}
export function Tabs({ children }: ITabsProps) {
  const [selectedTabIndex, setSelectedTabIndex] = useAtom(selectedTabIndexState);
  return (
    <HeadlessTab.Group selectedIndex={selectedTabIndex} onChange={(index) => setSelectedTabIndex(index)}>
      {children}
    </HeadlessTab.Group>
  );
}

export function TabPanel(props: Parameters<typeof HeadlessTab.Panel>[0]) {
  return <HeadlessTab.Panel {...props} />;
}

interface IMobileTabListProps {
  children: ReactNode;
}

export function MobileTabList({ children, ...props }: Parameters<typeof HeadlessTab.List>[0] & IMobileTabListProps) {
  const selectId = useId();
  const [selectedTabIndex, setSelectedTabIndex] = useAtom(selectedTabIndexState);
  return (
    <div className="sm:hidden">
      <label htmlFor={selectId} className="sr-only">
        Selecione uma aba
      </label>

      <HeadlessTab.List {...props} as={Fragment}>
        {() => {
          return (
            <select
              id={selectId}
              name={selectId}
              value={selectedTabIndex}
              onChange={(event) => setSelectedTabIndex(parseInt(event.target.value, 10) || 0)}
              className={classNames([
                "block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base",
                "focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm",
              ])}
            >
              {children}
            </select>
          );
        }}
      </HeadlessTab.List>
    </div>
  );
}

interface ITabListProps {
  children: ReactNode;
}

export function TabList({ children, ...props }: Parameters<typeof HeadlessTab.List>[0] & ITabListProps) {
  return (
    <div className="hidden sm:block">
      <div className="border-b border-gray-200">
        <HeadlessTab.List {...props} as={Fragment}>
          <nav className="-mb-px flex space-x-4" aria-label="Abas">
            {children}
          </nav>
        </HeadlessTab.List>
      </div>
    </div>
  );
}

interface IMobileTabProps {
  index: number;
  children: ReactNode;
}

export function MobileTab({ index, children, ...props }: Parameters<typeof HeadlessTab>[0] & IMobileTabProps) {
  return (
    <HeadlessTab {...props} as={Fragment}>
      {() => {
        return <option value={index}>{children}</option>;
      }}
    </HeadlessTab>
  );
}

interface ITabProps {
  children: ReactNode;
}

export function Tab({ children, ...props }: Parameters<typeof HeadlessTab>[0] & ITabProps) {
  return (
    <HeadlessTab {...props} as={Fragment}>
      {({ selected }) => {
        return (
          <button
            type="button"
            className={classNames(
              selected
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              "whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium",
              "focus:outline-none",
            )}
            aria-current={selected ? "page" : undefined}
          >
            {children}
          </button>
        );
      }}
    </HeadlessTab>
  );
}
